import { runInAction } from 'mobx';

import BaseListStore, { DEFAULT_LIMIT, DEFAULT_PAGE_NUMBER } from 'stores/BaseListStore';
import PolicyStore, { IPolicy, PolicyType, policyTypeToLabel } from 'stores/policy/PolicyStore';
import { IDataFetchOptions, IGetDataParams } from '../../types';
import { destroy, EntityName, get, getCount, getDetailsForCorrectPagination, getPage, patch, post } from 'stores/_services/dataService';
import { getConditions } from 'stores/_services/filterService';

const SEARCH_FIELDS = ['name', 'description'];

const DEFAULT_FILTER = { search: '', policyType: null };

export default class PolicyListStore extends BaseListStore<PolicyStore> {
    constructor() {
        super({
            itemStore: PolicyStore,
            defaultFilter: DEFAULT_FILTER
        });
    }

    EDR_POLICY_TYPES = [PolicyType.Edr, PolicyType.Defender, PolicyType.Ransomware, PolicyType.Response];

    async getPolicies(args: IDataFetchOptions = {}, pageNumber = DEFAULT_PAGE_NUMBER, pageSize = DEFAULT_LIMIT): Promise<void> {
        const shouldReset = this._updateListState(args);
        const conditions = getConditions(this.filter);

        const options = {
            filter: {
                searchFields: SEARCH_FIELDS,
                order: this.sort,
                limit: pageSize,
                skip: (pageNumber - 1) * pageSize,
                where: conditions
            }
        };

        const { data } = await getPage(EntityName.POLICIES, options);
        const totalCount = await getCount(EntityName.POLICIES, { where: conditions, searchFields: SEARCH_FIELDS });

        if (shouldReset) {
            this.clearIds();
        }

        this._updateData(data);
        this._updateIds(data, 0);

        runInAction(() => {
            this.count = data.length;
            this.totalCount = totalCount;
            this.page = pageNumber;
            this.pageSize = pageSize;
        });
    }

    async getPoliciesForCorrectPagination(args: IGetDataParams = {}): Promise<void> {
        const { filter, sortBy, sortDirection, pageSize, page } = args;

        const params = {
            filter: filter || this.filter,
            sortBy: sortBy || this.sortBy,
            sortDirection: sortDirection || this.sortDirection,
            pageSize: pageSize || this.pageSize,
            page: page || this.page,
            searchFields: SEARCH_FIELDS
        };

        const { data, totalCount } = await getDetailsForCorrectPagination<PolicyListStore>({
            params,
            entityName: EntityName.POLICIES
        });

        this.updateStateForCorrectPagination<PolicyListStore>({ args, data, totalCount });
    }

    async getResponsePolicies(): Promise<void> {
        const { data } = await get(EntityName.POLICIES, { filter: { where: { type: 'response' } }, fields: ['id', 'name'] });

        this.clearIds();
        this._updateData(data);
        this._updateIds(data, 0);
    }

    async somePolicyExists(): Promise<boolean> {
        const count = await getCount(EntityName.POLICIES);
        return !!count;
    }

    async getPolicyData(policyId: string): Promise<void> {
        const res = await get(`${EntityName.POLICIES}/${policyId}`);

        const { data } = res;

        this._updateData({
            id: policyId,
            data: data.data,
            name: data.name,
            description: data.description,
            type: data.type,
            active: data.active,
            createdOn: data.createdOn,
            updatedOn: data.updatedOn
        });
    }

    async createPolicy(policy: IPolicy): Promise<void> {
        const payload = {
            name: policy.name,
            description: policy.description,
            type: policy.type
        };

        const result = await post(EntityName.POLICIES, payload);
        return result;
    }

    async clonePolicy(policyId: string, policyMetadata: any): Promise<void> {
        const result = await post(`${EntityName.POLICIES}/${policyId}/clone`, policyMetadata);
        return result;
    }

    async savePolicy(policy: IPolicy): Promise<void> {
        const payload = {
            id: policy.id,
            name: policy.name,
            description: policy.description,
            type: policy.type,
            data: policy.data,
            active: policy.active
        };

        const result = await patch(`${EntityName.POLICIES}/${policy.id}`, payload);
        return result;
    }

    async toggleEnabled(policyId: string): Promise<void> {
        const policy = this.data.get(policyId)?.policy;

        const payload = {
            id: policyId,
            active: !policy?.active,
            name: policy?.name,
            type: policy?.type
        };

        await post(`${EntityName.POLICIES}/toggleEnabled`, payload);
    }

    async toggleOption(payload: IPolicy): Promise<void> {
        await patch(`${EntityName.POLICIES}/${payload.id}`, { data: { ...payload.data } });
    }

    async addExclusion(policyId: string, exclusionPath: string): Promise<void> {
        const payload = {
            policyId,
            exclusionPath
        };

        await post('policies/addExclusion', payload);
    }

    async checkAssignmentCount(policyId: string): Promise<number> {
        const { data } = await get(`${EntityName.POLICIES}/${policyId}/assignmentCount`);
        return data?.totalAssignments;
    }

    async deletePolicy(policyId: string): Promise<void> {
        await destroy(`${EntityName.POLICIES}/${policyId}`);
        await this.getPolicies({ reset: true });
    }

    async toggleDefault(policyId: string, setDefault: boolean): Promise<void> {
        const payload = {
            policyId: policyId,
            isDefault: setDefault
        };

        await post(`${EntityName.POLICIES}/setDefault`, payload);
        await this.getPolicies({ reset: true });
    }

    async assignPolicy(policyId: string, organizationId?: string, locationId?: string, deviceGroupId?: string): Promise<void> {
        const payload = {
            filter: {
                where: { id: policyId }
            },
            organizationId,
            locationId,
            deviceGroupId
        };

        locationId
            ? await post(`${EntityName.POLICIES}/assignToLocation`, payload)
            : await post(`${EntityName.POLICIES}/assignToOrganization/${organizationId}`, payload);
        await this.getPolicies({ reset: true });
    }

    getEnabledPolicyTypes(dattoAvEnabled: boolean, dattoEdrEnabled: boolean): Array<any> {
        let enabledPolicyTypes: any = {};

        if (dattoAvEnabled) {
            enabledPolicyTypes[PolicyType.Av] = true;
        }

        if (dattoEdrEnabled) {
            enabledPolicyTypes[PolicyType.Edr] = true;
            enabledPolicyTypes[PolicyType.Defender] = true;
            enabledPolicyTypes[PolicyType.Ransomware] = true;
            enabledPolicyTypes[PolicyType.Response] = true;
        }

        return enabledPolicyTypes;
    }

    getPolicyOptions(dattoAvEnabled: boolean, dattoEdrEnabled: boolean): Array<any> {
        const enabledPolicyTypes = this.getEnabledPolicyTypes(dattoAvEnabled, dattoEdrEnabled);

        const policyOptions = Object.keys(enabledPolicyTypes).map((key) => ({
            value: key,
            label: policyTypeToLabel[key]
        }));

        return policyOptions;
    }

    async getDnsThreatCategories() {
        const { data } = await get('policies/getDnsSecureCategories', {});
        return data;
    }
}
